import { useEffect, useRef, useState } from "react";
import { Camera, KeyRound, Loader2, Save, ShieldCheck, UserRound } from "lucide-react";

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import {
  useChangePassword,
  useUpdateProfile,
  type ProfileInput,
} from "@/hooks/useProfile";
import { initials } from "@/lib/format";

const ROLE_VARIANT = {
  admin:    "destructive",
  ceo:      "default",
  director: "secondary",
  user:     "outline",
} as const;

/** /profile — current user's settings. Two cards stacked: identity
 *  (name, contact, bio, avatar) and security (change password). */
export default function Profile() {
  const { data: user } = useCurrentUser();
  if (!user) return null;

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <header className="flex items-start gap-3">
        <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-gradient-to-br from-primary to-emerald-700 text-primary-foreground shadow-soft">
          <UserRound className="h-5 w-5" />
        </div>
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Profile</h1>
          <p className="text-sm text-muted-foreground">
            Your name, contact, and password. Visible to colleagues across the staff portal.
          </p>
        </div>
      </header>

      {user.must_change_password && (
        <div className="rounded-md border border-amber-200 dark:border-amber-500/30 bg-amber-50 dark:bg-amber-500/15 px-4 py-3 text-sm text-amber-800 dark:text-amber-300">
          <strong>Action needed:</strong> please change your password — your administrator flagged
          this account as needing a fresh credential.
        </div>
      )}

      <ProfileCard />
      <PasswordCard />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Profile fields
// ---------------------------------------------------------------------------

function ProfileCard() {
  const { data: user } = useCurrentUser();
  const update = useUpdateProfile();

  const [firstName, setFirstName] = useState("");
  const [lastName,  setLastName]  = useState("");
  const [phone,     setPhone]     = useState("");
  const [position,  setPosition]  = useState("");
  const [bio,       setBio]       = useState("");
  const [avatar,    setAvatar]    = useState<File | null>(null);
  const [preview,   setPreview]   = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!user) return;
    setFirstName(user.first_name || "");
    setLastName(user.last_name || "");
    setPhone(user.phone || "");
    setPosition(user.position || "");
    setBio(user.bio || "");
    setAvatar(null);
    setPreview(null);
    update.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  function onAvatar(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0] ?? null;
    setAvatar(f);
    setPreview(f ? URL.createObjectURL(f) : null);
  }

  function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    const input: ProfileInput = {
      first_name: firstName.trim(),
      last_name:  lastName.trim(),
      phone:      phone.trim(),
      position:   position.trim(),
      bio:        bio.trim(),
    };
    if (avatar) input.avatar = avatar;
    update.mutate(input, {
      onSuccess: () => {
        setAvatar(null);
        setPreview(null);
        if (fileInputRef.current) fileInputRef.current.value = "";
      },
    });
  }

  if (!user) return null;

  const err = update.error as
    | { response?: { data?: Record<string, unknown> | { detail?: string } } }
    | undefined;
  const errorMsg = formatApiError(err);

  return (
    <Card className="border-border/60 shadow-soft">
      <CardHeader className="border-b border-border/60 bg-muted/30">
        <CardTitle className="text-base">Identity</CardTitle>
        <CardDescription>
          Your name, contact details, and short bio.
        </CardDescription>
      </CardHeader>
      <CardContent className="p-6">
        <form onSubmit={onSubmit} className="space-y-5">
          {/* Avatar row */}
          <div className="flex items-center gap-4">
            <Avatar className="h-20 w-20 ring-2 ring-border">
              {/* `preview` is the blob URL of a freshly-picked file
                  before save. Once the upload completes we reset
                  preview → null, so fall back to user.avatar_url so the
                  saved photo stays visible on this page (every other
                  surface already reads it directly). */}
              {(preview || user.avatar_url) && (
                <AvatarImage src={preview ?? user.avatar_url ?? undefined} />
              )}
              <AvatarFallback className="bg-gradient-to-br from-primary to-emerald-700 text-base font-semibold text-primary-foreground">
                {initials(user.full_name || user.email)}
              </AvatarFallback>
            </Avatar>
            <div className="space-y-1">
              <Label htmlFor="avatar-input" className="text-sm font-medium">Profile photo</Label>
              <div className="flex items-center gap-2">
                <input
                  ref={fileInputRef}
                  id="avatar-input" type="file" accept="image/*"
                  className="sr-only"
                  onChange={onAvatar}
                />
                <Button
                  type="button" variant="outline" size="sm"
                  onClick={() => fileInputRef.current?.click()}
                >
                  <Camera className="mr-2 h-4 w-4" />Choose image
                </Button>
                {avatar && <span className="text-xs text-muted-foreground">{avatar.name}</span>}
              </div>
              <p className="text-xs text-muted-foreground">
                JPEG or PNG, up to ~10 MB. Shown next to your name in the topbar and threads.
              </p>
            </div>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="pf-first">First name</Label>
              <Input
                id="pf-first" maxLength={100}
                value={firstName} onChange={(e) => setFirstName(e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="pf-last">Last name</Label>
              <Input
                id="pf-last" maxLength={100}
                value={lastName} onChange={(e) => setLastName(e.target.value)}
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="pf-phone">Phone</Label>
              <Input
                id="pf-phone" maxLength={30}
                value={phone} onChange={(e) => setPhone(e.target.value)}
                placeholder="+251 ..."
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="pf-position">Position</Label>
              <Input
                id="pf-position" maxLength={120}
                value={position} onChange={(e) => setPosition(e.target.value)}
                placeholder="e.g. Climate Finance Analyst"
              />
            </div>

            <div className="space-y-1.5 sm:col-span-2">
              <Label htmlFor="pf-bio">Bio</Label>
              <Textarea
                id="pf-bio" rows={3} maxLength={500}
                value={bio} onChange={(e) => setBio(e.target.value)}
                placeholder="A short blurb shown on your profile and in the staff directory."
              />
              <p className="text-right text-xs text-muted-foreground tabular-nums">
                {bio.length} / 500
              </p>
            </div>
          </div>

          {/* Read-only fields */}
          <div className="grid gap-4 rounded-md border bg-muted/30 p-4 sm:grid-cols-2">
            <ReadOnlyField label="Email" value={user.email} />
            <ReadOnlyField
              label="Role"
              value={
                <Badge variant={ROLE_VARIANT[user.role]} className="uppercase">
                  <ShieldCheck className="mr-1 h-3 w-3" />{user.role}
                </Badge>
              }
            />
            <ReadOnlyField label="Department" value={user.department_name || "—"} />
          </div>

          {errorMsg && (
            <div className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
              {errorMsg}
            </div>
          )}
          {update.isSuccess && !update.isPending && (
            <div className="rounded-md border border-emerald-200 dark:border-emerald-500/30 bg-emerald-50 dark:bg-emerald-500/15 px-3 py-2 text-sm text-emerald-800 dark:text-emerald-300">
              Profile saved.
            </div>
          )}

          <div className="flex justify-end">
            <Button type="submit" disabled={update.isPending}>
              {update.isPending
                ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Saving…</>
                : <><Save className="mr-2 h-4 w-4" />Save changes</>}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}

function ReadOnlyField({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="space-y-0.5">
      <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
        {label}
      </div>
      <div className="text-sm font-medium">{value}</div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Change password
// ---------------------------------------------------------------------------

function PasswordCard() {
  const change = useChangePassword();
  const [current, setCurrent] = useState("");
  const [next1,   setNext1]   = useState("");
  const [next2,   setNext2]   = useState("");
  const [showSaved, setShowSaved] = useState(false);

  const mismatch = next1.length > 0 && next2.length > 0 && next1 !== next2;
  const canSubmit = current.length > 0 && next1.length >= 8 && !mismatch && !change.isPending;

  function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!canSubmit) return;
    change.mutate(
      { current_password: current, new_password: next1 },
      {
        onSuccess: () => {
          setCurrent(""); setNext1(""); setNext2("");
          setShowSaved(true);
          setTimeout(() => setShowSaved(false), 5000);
        },
      },
    );
  }

  const err = change.error as
    | { response?: { data?: Record<string, unknown> | { detail?: string } } }
    | undefined;
  const errorMsg = formatApiError(err);

  return (
    <Card className="border-border/60 shadow-soft">
      <CardHeader className="border-b border-border/60 bg-muted/30">
        <CardTitle className="text-base">Password</CardTitle>
        <CardDescription>
          Use at least 8 characters. Don't reuse passwords from other sites.
        </CardDescription>
      </CardHeader>
      <CardContent className="p-6">
        <form onSubmit={onSubmit} className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="pw-current">Current password</Label>
            <Input
              id="pw-current" type="password" required autoComplete="current-password"
              value={current} onChange={(e) => setCurrent(e.target.value)}
            />
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="pw-new1">New password</Label>
              <Input
                id="pw-new1" type="password" required autoComplete="new-password"
                minLength={8}
                value={next1} onChange={(e) => setNext1(e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="pw-new2">Confirm new password</Label>
              <Input
                id="pw-new2" type="password" required autoComplete="new-password"
                minLength={8}
                value={next2} onChange={(e) => setNext2(e.target.value)}
              />
              {mismatch && (
                <p className="text-xs text-destructive">Passwords don't match.</p>
              )}
            </div>
          </div>

          {errorMsg && (
            <div className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
              {errorMsg}
            </div>
          )}
          {showSaved && (
            <div className="rounded-md border border-emerald-200 dark:border-emerald-500/30 bg-emerald-50 dark:bg-emerald-500/15 px-3 py-2 text-sm text-emerald-800 dark:text-emerald-300">
              Password changed.
            </div>
          )}

          <div className="flex justify-end">
            <Button type="submit" disabled={!canSubmit}>
              {change.isPending
                ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Updating…</>
                : <><KeyRound className="mr-2 h-4 w-4" />Change password</>}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Shared error formatter
// ---------------------------------------------------------------------------

function formatApiError(
  err: { response?: { data?: Record<string, unknown> | { detail?: string } } } | undefined,
): string | null {
  if (!err?.response?.data) return null;
  const data = err.response.data as Record<string, unknown> & { detail?: string };
  if (typeof data.detail === "string") return data.detail;
  for (const [field, msgs] of Object.entries(data)) {
    if (Array.isArray(msgs) && msgs.length && typeof msgs[0] === "string") {
      return msgs[0] as string;
    }
    if (typeof msgs === "string") return `${field}: ${msgs}`;
  }
  return "Something went wrong.";
}
